import type { CollectionState, ComparableArtist } from "@/api/discover";

export type EvidenceStatus = {
  label: string;
  tone: "ok" | "muted" | "warn" | "error" | "busy";
  /** Primary evidence action for this state, or null when an update isn't useful. */
  action: "find" | "refresh" | null;
};

/** Status for a comparable card, derived from stored state (no provider call). */
export function evidenceStatus(comparable: ComparableArtist): EvidenceStatus {
  const pa = comparable.public_artist;
  if (!pa.has_collection_identifier) {
    return { label: "Live history not available for this artist", tone: "muted", action: null };
  }
  if (pa.collection_in_progress) {
    return { label: "Updating…", tone: "busy", action: null };
  }
  if (pa.is_fresh) {
    return { label: "Up to date", tone: "ok", action: null };
  }
  if (pa.last_collected_at) {
    return { label: "Venue history may be out of date", tone: "warn", action: "refresh" };
  }
  if (pa.last_collection_status === "empty") {
    return { label: "No performance history found yet", tone: "muted", action: "find" };
  }
  if (pa.last_collection_status === "failed") {
    return { label: "Couldn't update last time", tone: "error", action: "find" };
  }
  return { label: "Venues not loaded yet", tone: "muted", action: "find" };
}

/** Message shown right after the user explicitly asked for an update. */
export function collectionResultMessage(result: CollectionState): { text: string; tone: EvidenceStatus["tone"] } {
  switch (result) {
    case "collected":
      return { text: "Updated", tone: "ok" };
    case "fresh":
      return { text: "Already up to date", tone: "ok" };
    case "in_progress":
      return { text: "Updating… check back in a moment", tone: "busy" };
    case "cooldown":
      return { text: "Recently checked — try again later", tone: "muted" };
    case "empty":
      return { text: "No performance history found yet", tone: "muted" };
    case "failed":
      return { text: "Couldn't update right now", tone: "error" };
    case "missing_identifier":
      return { text: "Live history isn't available for this artist", tone: "muted" };
    default:
      return { text: "Couldn't update right now", tone: "error" };
  }
}

export const PLAN_REQUIRED_MESSAGE =
  "Your free trial has ended. Choose a plan in Settings → Billing to keep using this.";

export function isPlanRequired(error: unknown): boolean {
  const err = error as {
    code?: string;
    payload?: { error?: string; detail?: string | { error?: string } };
  } | null;
  const detail = err?.payload?.detail;
  return (
    err?.code === "capability_required" ||
    err?.payload?.error === "requires_pro" ||
    (typeof detail === "object" && detail?.error === "requires_pro")
  );
}

export function friendlyError(error: unknown, fallback: string): string {
  const status = (error as { status?: number } | null)?.status;
  if (isPlanRequired(error)) return PLAN_REQUIRED_MESSAGE;
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have access to do that for this artist.";
  if (status === 404) return "That item is no longer available.";
  if ((error as { code?: string } | null)?.code === "timeout") return "This is taking longer than expected. Please try again.";
  return fallback;
}

const MONTH_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
const DAY_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatMonth(value: string | null | undefined): string {
  const date = parseDate(value);
  return date ? MONTH_FORMAT.format(date) : "—";
}

export function formatDay(value: string | null | undefined): string {
  const date = parseDate(value);
  return date ? DAY_FORMAT.format(date) : "—";
}

export function formatLocation(parts: (string | null | undefined)[]): string {
  return parts.filter((part) => part && part.trim()).join(", ");
}

const DAY_MS = 24 * 60 * 60 * 1000;
const utcDay = (date: Date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/**
 * Deterministic, text-only contact recency (UTC calendar days, like the other Discover dates):
 * today, yesterday, N days (<7), N weeks (<30 days), N months (<365 days), then "Mon YYYY".
 */
export function formatContactedRecency(value: string | null | undefined, now: Date = new Date()): string {
  const date = parseDate(value);
  if (!date) return "Contacted";
  const days = Math.floor((utcDay(now) - utcDay(date)) / DAY_MS);
  if (days <= 0) return "Contacted today";
  if (days === 1) return "Contacted yesterday";
  if (days < 7) return `Contacted ${plural(days, "day")} ago`;
  if (days < 30) return `Contacted ${plural(Math.floor(days / 7), "week")} ago`;
  if (days < 365) return `Contacted ${plural(Math.max(1, Math.floor(days / 30)), "month")} ago`;
  return `Contacted ${MONTH_FORMAT.format(date)}`;
}
